/*
 * ET - PDP Feature Cards (sections/et-pdp-feature-cards.liquid)
 * <et-pdp-feature-cards-grid> wraps the card grid and drives its motion:
 *  - is-in-view on each grid item ([data-et-pfc-item]) while any part of it is on screen. The CSS pauses the small
 *    loops (pulses, scan line, rays, ...) of items that are off-screen.
 *  - is-revealed, once, on each entrance target ([data-et-pfc-reveal]: the hero images, the guest cards) when half of
 *    it is visible. Until then the CSS keeps the side images / side cards tucked behind the front one.
 *  - the 180° handle spotlight ([data-et-pfc-handle][data-animate]): the handle turns from 0° to data-degrees and
 *    back, the degree ring and the counter follow it; it runs only while its card is in view and the tab is visible,
 *    and resumes where it paused.
 * Nothing is armed for visitors who prefer reduced motion or without IntersectionObserver: the markup is the final
 * state (images in place, handle at the full rotation). A reduced-motion change at runtime tears everything down or
 * sets it up again. As a custom element it re-runs on every connect, so a theme editor re-render
 * (shopify:section:load) sets up the new grid and the removed one disconnects its observers and animation frame.
 * Loaded by the section only when something in it animates.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('et-pdp-feature-cards-grid')) return;

  const ARMED_CLASS = 'et-pdp-feature-cards__grid--armed';
  const IN_VIEW_CLASS = 'is-in-view';
  const REVEALED_CLASS = 'is-revealed';
  const reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  // Handle cycle (ms): rest at 0°, turn, hold at the full rotation, turn back, rest.
  const HOLD_START = 400;
  const FORWARD = 1800;
  const HOLD_END = 2000;
  const REVERSE = 1800;
  const HOLD_RESET = 400;
  const CYCLE = HOLD_START + FORWARD + HOLD_END + REVERSE + HOLD_RESET;

  const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  class HandleLoop {
    constructor(root) {
      this.max = parseFloat(root.dataset.degrees) || 180;
      this.arm = root.querySelector('[data-et-pfc-handle-arm]');
      this.ring = root.querySelector('[data-et-pfc-handle-ring]');
      this.number = root.querySelector('[data-et-pfc-handle-number]');
      this.circumference = this.ring ? 2 * Math.PI * this.ring.r.baseVal.value : 0;
      this.frame = null;
      this.start = 0;
      this.elapsed = 0;
      this.tick = this.tick.bind(this);
    }

    angleAt(time) {
      let t = time;
      if (t < HOLD_START) return 0;
      t -= HOLD_START;
      if (t < FORWARD) return ease(t / FORWARD) * this.max;
      t -= FORWARD;
      if (t < HOLD_END) return this.max;
      t -= HOLD_END;
      if (t < REVERSE) return this.max - ease(t / REVERSE) * this.max;
      return 0;
    }

    render(angle) {
      if (this.arm) this.arm.style.transform = `rotate(${angle}deg)`;
      if (this.ring) this.ring.style.strokeDashoffset = String(this.circumference - (angle / 360) * this.circumference);
      if (this.number) this.number.textContent = String(Math.round(angle));
    }

    play() {
      if (this.frame !== null) return;
      this.frame = requestAnimationFrame((now) => {
        this.start = now - this.elapsed;
        this.tick(now);
      });
    }

    tick(now) {
      this.elapsed = (now - this.start) % CYCLE;
      this.render(this.angleAt(this.elapsed));
      this.frame = requestAnimationFrame(this.tick);
    }

    pause() {
      if (this.frame === null) return;
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    // Back to the markup's pose: the full rotation
    reset() {
      this.pause();
      this.elapsed = 0;
      if (this.arm) this.arm.style.removeProperty('transform');
      if (this.ring) this.ring.style.removeProperty('stroke-dashoffset');
      if (this.number) this.number.textContent = String(Math.round(this.max));
    }
  }

  class EtPdpFeatureCardsGrid extends HTMLElement {
    connectedCallback() {
      this.onMotionChange = this.onMotionChange.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      if (reducedMotion) {
        if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', this.onMotionChange);
        else if (reducedMotion.addListener) reducedMotion.addListener(this.onMotionChange);
      }
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.setup();
    }

    disconnectedCallback() {
      this.teardown();
      if (reducedMotion) {
        if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', this.onMotionChange);
        else if (reducedMotion.removeListener) reducedMotion.removeListener(this.onMotionChange);
      }
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    setup() {
      if (reducedMotion && reducedMotion.matches) return;
      if (!('IntersectionObserver' in window)) return;

      this.items = Array.from(this.querySelectorAll('[data-et-pfc-item]'));
      this.revealTargets = Array.from(this.querySelectorAll('[data-et-pfc-reveal]'));
      if (!this.items.length) return;

      this.handles = new Map();
      this.items.forEach((item) => {
        const root = item.querySelector('[data-et-pfc-handle][data-animate]');
        if (!root) return;
        const loop = new HandleLoop(root);
        loop.render(0);
        this.handles.set(item, loop);
      });

      this.classList.add(ARMED_CLASS);

      this.viewObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle(IN_VIEW_CLASS, entry.isIntersecting);
          const loop = this.handles.get(entry.target);
          if (!loop) return;
          if (entry.isIntersecting && !document.hidden) loop.play();
          else loop.pause();
        });
      });
      this.items.forEach((item) => this.viewObserver.observe(item));

      if (this.revealTargets.length) {
        this.revealObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add(REVEALED_CLASS);
              this.revealObserver.unobserve(entry.target);
            });
          },
          { threshold: 0.5 }
        );
        this.revealTargets.forEach((target) => this.revealObserver.observe(target));
      }
    }

    teardown() {
      if (this.viewObserver) this.viewObserver.disconnect();
      if (this.revealObserver) this.revealObserver.disconnect();
      this.viewObserver = null;
      this.revealObserver = null;
      if (this.handles) this.handles.forEach((loop) => loop.reset());
      this.handles = null;
      (this.items || []).forEach((item) => item.classList.remove(IN_VIEW_CLASS));
      (this.revealTargets || []).forEach((target) => target.classList.remove(REVEALED_CLASS));
      this.items = null;
      this.revealTargets = null;
      this.classList.remove(ARMED_CLASS);
    }

    onMotionChange() {
      this.teardown();
      this.setup();
    }

    onVisibilityChange() {
      if (!this.handles) return;
      this.handles.forEach((loop, item) => {
        if (document.hidden) loop.pause();
        else if (item.classList.contains(IN_VIEW_CLASS)) loop.play();
      });
    }
  }

  customElements.define('et-pdp-feature-cards-grid', EtPdpFeatureCardsGrid);
})();
